<template>
  <primary-photo-and-gallery
    v-observe-visibility="{
      callback: visibilityChanged,
    }"
    data-test="photo-and-gallery"
    :search-result-index="searchResultIndex"
    :gallery-full-screen="galleryFullScreen"
    :photos="photos"
    :primary-photo="primaryPhoto"
    :campsite-name="campsiteName"
    :photo-count="photoCountToDisplay"
    :show-search-gallery="showSearchGallery"
    :gallery-initial-index="galleryInitialIndex"
    :is-search-results="isSearchResults"
    @click="$emit('click')"
    @user-has-interacted.once="userInteractionHandler"
    @mouseover.once="userInteractionHandler"
  />
</template>

<script lang="ts">
import type { PitchupPhoto } from '../../../apps/campsite-photos/types'
import { PhotoLoaderService } from '~/common/photos/photoLoader'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import isNotAnonAxiosError from '~/utility/isNotAnonAxiosError'

@Component
class PuCampsitePhotosProvider extends Vue {
  @Prop()
  primaryPhoto: PitchupPhoto

  @Prop()
  campsiteId: string

  @Prop()
  campsiteName: string

  @Prop()
  categoryIds: string[]

  @Prop({ default: false })
  isSearchResults: boolean

  @Prop({ default: false })
  galleryFullScreen: boolean

  @Prop({ required: false })
  searchResultIndex: number

  @Prop({ required: false })
  showSearchGallery: boolean

  @Prop({ required: false })
  photoCount: number

  @Prop({ required: false })
  galleryInitialIndex: number

  // if you pass in the photos it won't fetch them
  @Prop({ default: () => [] })
  photosProp: PitchupPhoto[]

  @Prop({ default: false })
  loadGallery: boolean

  @Prop({ default: false })
  loadPhotosOnMount: boolean

  campsitePhotos: PitchupPhoto[] = []

  isVisible = false
  userHasInteracted = false

  async mounted() {
    if (this.loadPhotosOnMount) {
      await this.loadPhotos()
    }
  }

  @Watch('loadGallery')
  async loadGalleryChange(loadGallery) {
    if (this.$.isUnmounted) return
    if (loadGallery) {
      await this.userInteractionHandler()
    }
  }

  @Watch('photosProp')
  async photosPropChange(newPhotosProp) {
    if (this.$.isUnmounted) return
    if (!newPhotosProp.length && (!this.photos || !this.photos.length)) {
      await this.loadPhotos()
    }
  }

  loadingPhotos = false

  async loadPhotos() {
    if (this.loadingPhotos) {
      return
    }
    this.loadingPhotos = true
    const photoLoaderService = new PhotoLoaderService()
    try {
      const photos = await photoLoaderService.loadPhotos(
        this.campsiteId,
        this.$i18n.locale,
      )
      if (this.$.isUnmounted) return
      this.campsitePhotos = photos
      this.userHasInteracted = true
    } catch (e) {
      const err = e as Error
      if (isNotAnonAxiosError(err)) {
        console.error(err, JSON.stringify(err))
      }
    }
  }

  get photos() {
    const photos =
      this.campsitePhotos && this.campsitePhotos.length > 0
        ? this.campsitePhotos
        : this.photosProp || []
    return photos
  }

  get photoCountToDisplay() {
    if (this.loadPhotosOnMount) {
      if (this.campsitePhotos) {
        return this.campsitePhotos.length
      }
      return 0
    }
    if (this.photoCount !== undefined) {
      return this.photoCount
    } else if (!this.userHasInteracted) {
      return this.photos.length
    } else {
      return undefined
    }
  }

  async visibilityChanged(isVisible: boolean) {
    if (this.$.isUnmounted) return
    this.isVisible = isVisible
    // $isDesktop is true for a tablet but we don't have a mouse
    // to load photos onmouseover so if we have ontouchstart event
    // load photos when visible.
    if (
      (!this.$isDesktop || 'ontouchstart' in window) &&
      isVisible &&
      (!this.photos || !this.photos.length)
    ) {
      await this.loadPhotos()
    }
  }

  async userInteractionHandler() {
    if (this.$.isUnmounted) return
    if (!this.photos || !this.photos.length) {
      await this.loadPhotos()
        .then(() => {
          if (this.$.isUnmounted) return
          this.userHasInteracted = true
        })
        .catch((e) => {
          if (isNotAnonAxiosError(e)) {
            console.error(e)
          }
        })
    } else {
      this.userHasInteracted = true
    }
  }
}

export default toNative(PuCampsitePhotosProvider)
</script>
